import { ClockIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import React from "react";
import { useTranslation } from "react-i18next";
import type { DealResponseDto } from "../../../types/types";
import { DealPaymentBreakdown } from "./dealPaymentBreakdown";
import {
  canFighterActOnDeal,
  canManagerWithdrawDeal,
  formatEventDate,
  getEventName,
  getLocationString,
  getStatusConfig,
} from "./dealCardHelpers";

interface DealCardProps {
  deal: DealResponseDto;
  currentUserId?: string | number;
  userRole?: string;
  connectStatus: string | null;
  isProcessing: boolean;
  onboardProcessing: boolean;
  formatSport: (text: string) => string;
  onOpen: () => void;
  onEdit: () => void;
  onWithdraw: () => void;
  onAddAccountDetails: (e: React.MouseEvent) => void;
}

const ACTION_BUTTON =
  "flex flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-white transition-all active:scale-95";

interface DetailRowProps {
  label: string;
  value?: string;
}

/** Renders nothing when there is no value, so empty rows are hidden. */
const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex gap-4 text-sm">
      <dt className="w-28 shrink-0 text-gray-500 sm:w-[140px]">{label}</dt>
      <dd className="min-w-0 break-words text-gray-900">{value}</dd>
    </div>
  );
};

export const DealCard: React.FC<DealCardProps> = ({
  deal,
  currentUserId,
  userRole,
  connectStatus,
  isProcessing,
  onboardProcessing,
  formatSport,
  onOpen,
  onEdit,
  onWithdraw,
  onAddAccountDetails,
}) => {
  const { t, i18n } = useTranslation("chat");

  const core = deal.formData?.core;
  const details = deal.formData?.details;

  const statusLabel =
    deal.status === "CANCELLED"
      ? t("withdrawn")
      : t(`dealStatus.${deal.status}`, {
          defaultValue: deal.status.replace(/_/g, " ").toLowerCase(),
        });

  const needsStripe =
    canFighterActOnDeal(deal, currentUserId) &&
    (connectStatus === "NEEDS_ONBOARDING" ||
      connectStatus === "VERIFICATION_PENDING");
  const canManagerWithdraw = canManagerWithdrawDeal(
    deal,
    currentUserId,
    userRole,
  );

  return (
    <div
      onClick={onOpen}
      className="w-full max-w-[400px] cursor-pointer rounded-3xl border border-gray-200 bg-white p-4 text-left font-['Plus_Jakarta_Sans']"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50">
            <DocumentTextIcon className="h-4 w-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold leading-5 text-gray-900">
              {t("dealDetails")}
            </p>
            {deal.dealId && (
              <p className="truncate text-xs text-gray-500">{deal.dealId}</p>
            )}
          </div>
        </div>
        <span
          className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${getStatusConfig(deal.status).classes}`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="my-3 border-t border-gray-100" />

      <dl className="space-y-2.5">
        <DetailRow
          label={t("contractType")}
          value={deal.dealType ? t(`dealTypes.${deal.dealType}`) : undefined}
        />
        <DetailRow
          label={t("fightStyle")}
          value={details?.sport ? formatSport(String(details.sport)) : undefined}
        />
        <DetailRow label={t("eventName")} value={getEventName(deal) ?? undefined} />
        <DetailRow
          label={t("eventLocation")}
          value={getLocationString(details?.location)}
        />
        <DetailRow
          label={t("date")}
          value={formatEventDate(details?.eventDate, i18n.language)}
        />
      </dl>

      <DealPaymentBreakdown deal={deal} currency={core?.currency ?? ""} />

      {(needsStripe || canManagerWithdraw) && (
        <div className="mt-4 flex gap-2">
          {needsStripe &&
            (connectStatus === "NEEDS_ONBOARDING" ? (
              <button
                className={`${ACTION_BUTTON} bg-indigo-600 hover:bg-indigo-700`}
                onClick={onAddAccountDetails}
                disabled={onboardProcessing}
              >
                {onboardProcessing ? (
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  t("addAccountDetails")
                )}
              </button>
            ) : (
              <button
                className={`${ACTION_BUTTON} cursor-not-allowed bg-amber-500`}
                disabled
              >
                <ClockIcon className="h-3 w-3" />
                {t("verificationPending")}
              </button>
            ))}

          {canManagerWithdraw && (
            <>
              <button
                className={`${ACTION_BUTTON} ${isProcessing ? "!bg-gray-300 !text-gray-500" : "bg-blue-500 hover:bg-blue-600"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                disabled={isProcessing}
              >
                <PencilIcon className="h-3 w-3" />
                {t("editButton")}
              </button>
              <button
                className={`${ACTION_BUTTON} ${isProcessing ? "!bg-gray-300 !text-gray-500" : "bg-orange-500 hover:bg-orange-600"}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onWithdraw();
                }}
                disabled={isProcessing}
              >
                <TrashIcon className="h-3 w-3" />
                {t("withdrawButton")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
